// NotesApp source file: category-cards.component.ts

import { Input, Output, EventEmitter, Component, HostListener, OnInit, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { computed, inject, SimpleChanges } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable, firstValueFrom } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SplitterModule } from 'primeng/splitter';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';

// Notes-App Services
import { Note, Category, DataService } from '../../../_service/data.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { LayoutService } from '../../../_service/layout.service';
import { LogService } from '../../../_service/log.service';
import { FilterParams } from '../../../_service/filter.service';
import { CategoryFilterStateService } from '../../../_service/category-filter-state.service';

type CardSort = {
  project_id: number;
  sort_order: number;
};

// Every combination of Note to Catgory, plus flag if it is to be displayed
type NoteCategory = {
  NoteID: string;
  CategoryID: string;
  isActive: boolean;
};

// Every combination of Note to Catgory, plus flag if it is to be displayed
type Card = {
  _id: string;
  Title: string;
  Type: string;
  CategoryId: string;
  SortOrder: number;
  isActive: boolean; // still needed?
  isExpanded: boolean;
  isVisible: boolean;
  Color?: string;
};

type NoteCard = {
  //?? necessary?
  NoteID: string;
  CardID: string;
  isActive: boolean;
};

type NoteIdentity = {
  _id?: string;
};

@Component({
  selector: 'app-category-cards',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    SplitterModule,
    TreeModule,
  ],
  templateUrl: './category-cards.component.html',
  styleUrls: ['./category-cards.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class CategoryCardsComponent implements OnInit, OnChanges {
  @Input() notes: Note[] = [];
  @Input() categories: Category[] = [];
  @Input() filter: FilterParams = {};
  @Input() filterCategories: string[] | undefined;
  @Input() isSelectMode: boolean = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() clickCategoryAction = new EventEmitter<Category>();
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();

  data: Note[] = [];
  paginatedData: Note[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  card_sort: CardSort[] = [];
  statusValues: string[] | undefined;
  types: string[] | undefined;
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  loadingCounter = 0; // initially count of loading objects in progress
  
  // Track last input fingerprints to prevent unnecessary rebuilds
  private lastNotesFingerprint: string = '';
  private lastCategoriesFingerprint: string = '';
  isOnline: boolean = true; //??
  favoriteNoteIds: string[] = [];
  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  //allNotesByCategory: NoteCategory[] = [];
  categoryTreeNodes: TreeNode[] = [];

  allNotesByCard: NoteCard[] = [];
  cards: Card[] = [];

  selectedTreeNodes: TreeNode[] = [];
  splitterStateKey = 'category-cards-splitter';
  isPanelCollapsed = false;
  isSmallScreen = false;
  panelSizes: number[] = [24, 76];
  minSizes: number[] = [16, 40];
  private lastPanelSizes: number[] | null = null;
  private readonly panelCollapsedKey = 'category-cards-panel-collapsed';
  private readonly panelSizesKey = 'category-cards-panel-sizes';

  synchLog$: Observable<string[]>;

  constructor(
    private router: Router,
    private logs: LogService,
    private userSettings: UserSettingsService,
    private categoryFilterState: CategoryFilterStateService,
    private dataService: DataService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
  }

  async ngOnInit() {
    console.log('CategoryCardsComponent - in ngOnInit(), filter categories:', 
        this.filterCategories);
    this.updateScreenSize();

    // Sort alphabetically by Path (case-insensitive)
    // this.categories.sort((a, b) => 
    //     (a.Path ?? '').toLowerCase().localeCompare((b.Path ?? '').toLowerCase()));
      this.categories.sort((a, b) => (a.SortOrder ?? 9999) - (b.SortOrder ?? 9999));

    this.userSettings.userSettings$.subscribe((settings) => {
      console.log('Home component - [UserSettingsService] initialize(): loaded', settings);
      if (settings?.primaryColor) {
        this.layoutService.appState.update((state: any) => ({
          ...state,
          primary: settings.primaryColor,
        }));
      }
      this.favoriteNoteIds = this.normalizeFavoriteNoteIds(settings?.favoriteNoteIds);
      if (this.notes?.length) {
        this.buildCards();
      }
    });
  
    this.loadPanelState();
    this.categoryFilterState.treeNodes$.subscribe((nodes) => {
      this.categoryTreeNodes = nodes;
    });
    this.categoryFilterState.selectedNodes$.subscribe((nodes) => {
      this.selectedTreeNodes = nodes;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('CategoryCardsComponent - in ngOnChanges()', changes);
    
    if (changes['notes'] && changes['notes'].currentValue) {
      // Create fingerprint to detect actual changes
      const newNotesFingerprint = this.createNotesFingerprint(this.notes);
      if (newNotesFingerprint !== this.lastNotesFingerprint) {
        console.log('[CategoryCards] Notes changed, rebuilding cards');
        this.lastNotesFingerprint = newNotesFingerprint;
        this.buildCards();
      } else {
        console.log('[CategoryCards] Notes unchanged (same fingerprint), skipping rebuild');
      }
    }

    if (changes['categories'] && changes['categories'].currentValue) {
      const newCategoriesFingerprint = this.createCategoriesFingerprint(this.categories);
      if (newCategoriesFingerprint !== this.lastCategoriesFingerprint) {
        console.log('[CategoryCards] Categories changed, updating state');
        this.lastCategoriesFingerprint = newCategoriesFingerprint;
        // Always resort case-insensitive when categories change
        this.categories.sort((a, b) => (a.SortOrder ?? 9999) - (b.SortOrder ?? 9999));
        this.categoryFilterState.setCategories(this.categories);
      } else {
        console.log('[CategoryCards] Categories unchanged (same fingerprint), skipping update');
      }
    }

    if (changes['filterCategories']) {
      this.categoryFilterState.setFilterCategories(this.filterCategories);
    }

    console.log('CategoryCardsComponent - updated allNotesByCard:', this.allNotesByCard);
  }

  /**
   * Create a fingerprint of notes to detect actual changes
   */
  private createNotesFingerprint(notes: Note[]): string {
    return notes
      .map((n) => {
        const categories = Array.isArray(n.Categories) ? [...n.Categories].sort().join(',') : '';
        return `${n._id}:${n.last_modified}:${n.Title ?? ''}:${n.Status ?? ''}:${categories}`;
      })
      .join('|');
  }

  /**
   * Create a fingerprint of categories to detect actual changes
   */
  private createCategoriesFingerprint(categories: Category[]): string {
    return categories
      .map((c) => `${c._id}:${c.Path}:${c.SortOrder}:${c.Parent ?? ''}:${c.Color ?? ''}`)
      .join('|');
  }



  insertCategoryCards(): void {
    const allCategoryIds = new Set(this.categories.map((c) => c._id));
    const validFilterCategoryIds = (this.filterCategories ?? []).filter((id) => allCategoryIds.has(id));
    const activeCategoryIds =
      validFilterCategoryIds.length === 0
        ? null // means: all categories active
        : new Set(validFilterCategoryIds);

    //let sortOrder = this.cards.length;

    for (const category of this.categories) {
      // if user has filtered by category, skip categories that are not active
      const isActive = activeCategoryIds === null || activeCategoryIds.has(category._id);

      if (!isActive) {
        continue;
      }

      let isVisible = false;
      let color = category.Color;
      let sortOrder = category.SortOrder ?? 9999;
      if (!category.Parent) {
        // top-level categories always visible
        isVisible = true;
      } else {
        // sub-categories visible only if parent category card is expanded
        const parentCard = this.cards.find((c) => c._id === category.Parent);
        if (!color) {
          color = parentCard ? parentCard.Color : undefined;
        }
        isVisible = parentCard ? parentCard.isExpanded : false;
      }

      this.cards.push({
        _id: category._id,
        Title: category.Path,
        Type: 'category',
        CategoryId: category._id, // redundant if main _id is category id
        SortOrder: sortOrder,
        isActive: true,
        isExpanded: this.getCardExpandedState(category._id, true),
        isVisible: isVisible,
        Color: color,
      });
    }
    this.cards.sort((a, b) => (a.SortOrder ?? 9999) - (b.SortOrder ?? 9999));
}

  /**
   * Toggle the expand/collapse state of a card
   */
  toggleCardExpanded(card: Card, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    card.isExpanded = !card.isExpanded;
    
    // Save to session storage
    this.saveCardExpandedState(card._id, card.isExpanded);
    
    // If this is a parent category, update visibility of child cards
    if (card.Type === 'category') {
      this.updateChildCardVisibility(card._id, card.isExpanded);
    }
  }

  /**
   * Save card expanded state to session storage
   */
  private saveCardExpandedState(cardId: string, isExpanded: boolean): void {
    try {
      const storageKey = 'category-cards-expanded-state';
      const existingState = sessionStorage.getItem(storageKey);
      const state = existingState ? JSON.parse(existingState) : {};
      state[cardId] = isExpanded;
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save card expanded state:', error);
    }
  }

  /**
   * Get card expanded state from session storage
   */
  private getCardExpandedState(cardId: string, defaultValue: boolean): boolean {
    try {
      const storageKey = 'category-cards-expanded-state';
      const existingState = sessionStorage.getItem(storageKey);
      if (existingState) {
        const state = JSON.parse(existingState);
        return state[cardId] !== undefined ? state[cardId] : defaultValue;
      }
    } catch (error) {
      console.error('Failed to retrieve card expanded state:', error);
    }
    return defaultValue;
  }

  /**
   * Update visibility of child category cards when parent is collapsed/expanded
   */
  private updateChildCardVisibility(parentCategoryId: string, isVisible: boolean): void {
    const childCategories = this.categories.filter(c => c.Parent === parentCategoryId);
    childCategories.forEach(childCat => {
      const childCard = this.cards.find(card => card._id === childCat._id);
      if (childCard) {
        childCard.isVisible = isVisible;
      }
    });
  }

  getCardColor(value?: string): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }

  isCategoryCardVisible(categoryId: string): boolean {
    const category = this.categories.find((c) => c._id === categoryId) ?? null;
    if (!category) {
      return false;
    }

    const card = this.cards.find((c) => c._id === categoryId);

    return card ? card.isVisible : false;
  }

  trackByCardId(_index: number, card: Card): string {
    return card._id;
  }

  trackByNoteId(_index: number, note: NoteIdentity): string {
    return note._id ?? `note-index-${_index}`;
  }

  // TO DO - update below function. Currently just prototype with hard-coded favorites.
  buildCards(): void {
    console.log('CategoryCardsComponent - in buildCards(), filter categories:', this.filterCategories);
    //const allNotesByCard: NoteCard[] = [];
    this.cards = [];
    this.allNotesByCard = [];

    const allCategoryIds = new Set(this.categories.map((c) => c._id));
    const validFilterCategoryIds = (this.filterCategories ?? []).filter((id) => allCategoryIds.has(id));
    const hasCategoryFilter = validFilterCategoryIds.length > 0;
    const activeCategoryIds = hasCategoryFilter ? new Set(validFilterCategoryIds) : null;

    // create card Favorites
    const favoritesCard: Card = {
      _id: 'card:favorites',
      Title: 'Favorites ❤️',
      Type: 'Favorites', // distinguish from category-based cards
      CategoryId: '', // virtual category
      SortOrder: -2, // ensures it appears at the top
      isActive: true,
      isExpanded: this.getCardExpandedState('card:favorites', true),
      isVisible: true,
    };
    this.cards.push(favoritesCard);
    const favoriteIdSet = new Set(this.favoriteNoteIds);
    this.notes.forEach((note) => {
      if (favoriteIdSet.has(note._id)) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:favorites',
          isActive: true,
        });
      }
    });
    // create card NextActions
    // create card Recent

    // create card Recent
    const recentCard: Card = {
      _id: 'card:recent',
      Title: 'Recent ⏰',
      Type: 'Recent', // distinguish from category-based cards
      CategoryId: '', // virtual category
      SortOrder: -1, // ensures it appears at the top
      isActive: true,
      isExpanded: this.getCardExpandedState('card:recent', true),
      isVisible: true,
    };
    this.cards.push(recentCard);
// add notes to recentCard that have been modified within last 5 days
    const now = new Date();
    this.notes.forEach((note) => {
      const modified = new Date(note.last_modified);
      const diffTime = Math.abs(now.getTime() - modified.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 5) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:recent',
          isActive: true,
        });
      }
    });

    // create card New This Week
    const newThisWeekCard: Card = {
      _id: 'card:new-this-week',
      Title: 'New This Week 🆕',
      Type: 'NewThisWeek',
      CategoryId: '',
      SortOrder: -1,
      isActive: true,
      isExpanded: this.getCardExpandedState('card:new-this-week', true),
      isVisible: true,
    };
    this.cards.push(newThisWeekCard);
    // add notes created within the last 7 days
    this.notes.forEach((note) => {
      const created = new Date(note.created_on);
      const diffTime = Math.abs(now.getTime() - created.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 7) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:new-this-week',
          isActive: true,
        });
      }
    });


    this.insertCategoryCards();
    // create cards based on Categories

    // insert notes into category cards
    this.notes.forEach((note) => {
      // console.log('Processing note:', note._id, note.Categories);
      note.Categories?.forEach((categoryID) => {
        // console.log(' → adding mapping:', note._id, '→', categoryID);
        if (!activeCategoryIds || activeCategoryIds.has(categoryID)) {
          this.allNotesByCard.push({
            NoteID: note._id,
            CardID: categoryID,
            isActive: true,
          });
        } // only if categoryID is not null or empty
      });
    });
    console.log('CardsComponent - updated cards:', this.cards);
  }

  private normalizeFavoriteNoteIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id) => typeof id === 'string');
  }

  get splitterSizes(): number[] {
    return this.panelSizes;
  }

  get splitterMinSizes(): number[] {
    return this.minSizes;
  }

  get splitterGutterSize(): number {
    return 6;
  }

  onSplitterResize(event: { sizes: (string | number)[] }) {
    if (this.isPanelCollapsed) {
      return;
    }
    if (Array.isArray(event.sizes) && event.sizes.length === 2) {
      this.panelSizes = event.sizes.map(s => typeof s === 'string' ? parseFloat(s) : s);
      localStorage.setItem(this.panelSizesKey, JSON.stringify(this.panelSizes));
    }
  }

  collapsePanel() {
    this.lastPanelSizes = [...this.panelSizes];
    this.isPanelCollapsed = true;
    this.panelSizes = [0, 100];
    this.persistSplitterState([0, 100]);
    localStorage.setItem(this.panelCollapsedKey, 'true');
  }

  expandPanel() {
    this.isPanelCollapsed = false;
    if (this.lastPanelSizes?.length === 2) {
      this.panelSizes = [...this.lastPanelSizes];
    }
    this.persistSplitterState(this.panelSizes);
    localStorage.setItem(this.panelCollapsedKey, 'false');
  }

  onTreeSelectionChange(nodes: TreeNode[] | TreeNode | null) {
    const selectedArray = Array.isArray(nodes) ? nodes : nodes ? [nodes] : [];
    this.selectedTreeNodes = selectedArray;
    this.categoryFilterState.updateFromSelectedNodes(selectedArray);
  }

  private loadPanelState() {
    const collapsed = localStorage.getItem(this.panelCollapsedKey);
    this.isPanelCollapsed = collapsed === 'true';
    const savedSizes = localStorage.getItem(this.panelSizesKey);
    if (savedSizes) {
      try {
        const parsed = JSON.parse(savedSizes);
        if (Array.isArray(parsed) && parsed.length === 2) {
          this.panelSizes = parsed;
        }
      } catch {
        // ignore malformed localStorage
      }
    }
    if (this.isPanelCollapsed) {
      this.panelSizes = [0, 100];
    }
  }

  private persistSplitterState(sizes: number[]) {
    if (!this.splitterStateKey) {
      return;
    }
    try {
      localStorage.setItem(this.splitterStateKey, JSON.stringify(sizes));
    } catch {
      // ignore storage errors
    }
  }

  @HostListener('window:resize')
  updateScreenSize() {
    this.isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

//   mapNotesToCategories(notes: Note[]): NoteCategory[] {
//     const allNotesByCategory: NoteCategory[] = [];

//     notes.forEach((note) => {
//       // console.log('Processing note:', note._id, note.Categories);
//       note.Categories?.forEach((categoryID) => {
//         // console.log(' → adding mapping:', note._id, '→', categoryID);
//         if (
//           !this.filterCategories ||
//           (this.filterCategories?.length > 0 && this.filterCategories?.includes(categoryID))
//         ) {
//           allNotesByCategory.push({
//             NoteID: note._id,
//             CategoryID: categoryID,
//             isActive: true,
//           });
//         } // only if categoryID is not null or empty
//       });
//     });
//     return allNotesByCategory;
//   }

  getAttachmentFlag(note: Note): string {
    const attachments = note.Attachments;
    return Array.isArray(attachments) && attachments.length > 0 ? ' 📎' : '';
  }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // If current day is less than the modified day, subtract one (not a full month yet)
    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

    getNotesForCard(cardId: string): Note[] {
    const noteIdSet = new Set(this.allNotesByCard.filter((nc) => nc.CardID === cardId).map((nc) => nc.NoteID));

    return this.notes
      .filter((note) => noteIdSet.has(note._id))
      .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
  }

//   getNotesForCategory(categoryId: string): Note[] {
//     const noteIdSet = new Set(
//       this.allNotesByCategory.filter((nc) => 
//         nc.CategoryID === categoryId).map((nc) => nc.NoteID),
//     );

//     return this.notes
//       .filter((note) => noteIdSet.has(note._id))
//       .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
//   }

//   getCategoryColor(value?: string): string {
//     if (!value) {
//       return '';
//     }
//     const trimmed = value.trim();
//     if (!trimmed) {
//       return '';
//     }
//     return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
//   }

  // count the number of filtered notes for a category
  calculateItemCount(cardID: string): number {
    //console.log("Count ",categoryID," = ",
    //  this.allNotesByCategory.filter(rec => rec.CategoryID === categoryID).length);
    return this.allNotesByCard.filter((rec) => rec.CardID === cardID).length;
  }

  //   toggleHeaderVisibility(): void {
  //     //console.log("toggle header now =",!this.isHeaderVisible);
  //     this.isHeaderVisible = !this.isHeaderVisible;
  //   }

  clickCardHeader(card: Card): void {
    console.log('clickCardHeader() - Category: ', card.Title);
    // Find the category object from the categories array
    const category = this.categories.find(cat => cat._id === card.CategoryId);
    if (category) {
      this.clickCategoryAction.emit(category);
    }
  }

  async createNoteForCategory(categoryId: string, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    try {
      const resp = await firstValueFrom(this.dataService.createNote());
      if (resp?.status !== 'success' || !resp.data?._id) {
        console.warn('Create note did not return an _id', resp);
        return;
      }
      const newNote = resp.data;
      newNote.Categories = [categoryId];
      await this.dataService.saveNoteOffline(newNote, false);
      this.router.navigateByUrl(`/edit/${encodeURIComponent(newNote._id)}`, {
        state: { filter: this.filter },
      });
    } catch (err) {
      console.error('Error creating note for category:', err);
    }
  }

  clickRow(row: Note) {
    if (this.isSelectMode) {
      this.toggleSelection(row);
      return;
    }

    console.log('clickRow() - Note: ', row.Title);
    this.router.navigate(['view', row._id], {
      state: { filter: this.filter },
    });
  }

  toggleSelection(note: Note, event?: MouseEvent) {
    event?.stopPropagation();
    console.log('toggleSelection() - Note: ', note.Title);
    const id = note._id;
    if (!id) {
      return;
    }

    const isAlreadySelected = this.isNoteSelected(id);
    const updatedSelection = isAlreadySelected
      ? this.selectedNoteIds.filter((n) => n !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(updatedSelection);
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  toggleSelectAllForCard(cardId: string, event?: MouseEvent) {
    event?.stopPropagation();
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return;
    }

    const allSelected = noteIds.every((id) => this.selectedNoteIds.includes(id));

    const nextSelection = allSelected
      ? this.selectedNoteIds.filter((id) => !noteIds.includes(id))
      : Array.from(new Set([...this.selectedNoteIds, ...noteIds]));

    this.selectedNoteIdsChange.emit(nextSelection);
  }

  areAllNotesSelectedForCard(cardId: string): boolean {
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return false;
    }

    return noteIds.every((id) => this.selectedNoteIds.includes(id));
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################
}
